import { AccountStatus, BrokerOfRecord } from '@newfront-insurance/account-api';
import { AuthRole } from '@newfront-insurance/auth-api';
import { useFuzzy } from '@newfront-insurance/core-ui';
import type { RouterQueryOutput } from '@newfront-insurance/data-layer-server';
import { useProvider } from '@newfront-insurance/react-provision';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';

import type { SharedRouter } from '../../../bff';
import { AdminTRPCProvider } from '../../../shared/providers/trpc';

export type Account = RouterQueryOutput<SharedRouter, 'accounts.getManyPublicAccounts'>[number];

/**
 * Some user roles can see all accounts on the account switcher because they work across
 * all accounts and are not part of a specific servicing team. i.e. Certificates.
 */
const GLOBAL_SEARCH_ALLOWED_USER_ROLES = [
  AuthRole.AM_MANAGER,
  AuthRole.BPO_MANAGER,
  AuthRole.BPO_WORKER,
  AuthRole.BPO_TECHTAMMINA,
  AuthRole.PRODUCER_MANAGER,
  AuthRole.SALES_MANAGER,
  AuthRole.ENGINEER,
];

function canUserSearchAllAcounts(scopes: string[]): boolean {
  return GLOBAL_SEARCH_ALLOWED_USER_ROLES.some((role) => scopes.includes(role));
}

interface UseAccountSwitcherOptions {
  loggedInUserUuid: string;
  userScopes: string[];
}

export interface UseAccountSwitcherOptionsResult {
  accounts: Account[];
  search: (searchText: string) => void;
  isLoading: boolean;
}

/**
 * Hook to fetch and search accounts for the account switcher.
 *
 * This hook implements proper ACL (Access Control List) handling:
 * - Users with specific roles (e.g., AM_MANAGER, ENGINEER) can search ALL accounts
 * - Other users can only see accounts they are related to via servicing team membership
 *
 * This ensures that users only see accounts they have permission to access.
 *
 * @param loggedInUserUuid - The UUID of the currently logged-in user
 * @param userScopes - The scopes/roles of the currently logged-in user
 * @returns Object containing accounts array, search function, and loading state
 */
export function useAccountSwitcherOptions({
  loggedInUserUuid,
  userScopes,
}: UseAccountSwitcherOptions): UseAccountSwitcherOptionsResult {
  const { useQuery } = useProvider(AdminTRPCProvider);
  const willSearchAllAccounts = canUserSearchAllAcounts(userScopes);

  const { data: accounts, isLoading: isLoadingAccounts } = useQuery(
    [
      'accounts.getManyPublicAccounts',
      {
        relatedUserUuid: loggedInUserUuid,
        brokerOfRecord: BrokerOfRecord.NEWFRONT,
        status: AccountStatus.ACTIVE,
      },
    ],
    {
      enabled: !willSearchAllAccounts,
    },
  );
  const { results: accountsForUser, search } = useFuzzy(accounts ?? [], { keys: ['name', 'dba'], debounce: 500 });

  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const debouncedGlobalSearch = useCallback(debounce(setGlobalSearchQuery, 500), [setGlobalSearchQuery]);
  const { data: allAccounts, isLoading: isLoadingAllAccounts } = useQuery(
    [
      'accounts.search',
      {
        query: globalSearchQuery,
        status: AccountStatus.ACTIVE,
      },
    ],
    {
      enabled: willSearchAllAccounts,
    },
  );

  if (willSearchAllAccounts) {
    return {
      accounts: allAccounts ?? [],
      isLoading: isLoadingAllAccounts,
      search: debouncedGlobalSearch,
    };
  }

  return {
    accounts: accountsForUser,
    isLoading: isLoadingAccounts,
    search,
  };
}
